import {
  abiaAcademyCheckService,
  abiaNewCandidateService,
  addCandidateAssessmentRequestData,
  fetchCandidateAssessmentInfoService,
} from "api";
import { Loader } from "components";
import { useApiRequest, useToast } from "hooks";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AbiaCheckRegistrationUI } from "features/assessments/viewAssessment/checkRegistration";

interface AssessmentData {
  title: string;
  id: string;
  _id: string;
  employer: { name: string; logo: string };
}

const CheckRegistration = () => {
  const { id } = useParams();
  const [showDetails, setShowDetails] = useState(false);
  const [candidateDetails, setCandidateDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    dob: "",
    gender: "",
    phone: "",
    assessmentStatus: "",
  });

  // Hooks
  const { openToast } = useToast();

  // API Request Hooks
  const { run, data: response, requestStatus, error } = useApiRequest({});
  const {
    run: runFetch,
    data: fetchResponse,
    requestStatus: fetchStatus,
    error: fetchError,
  } = useApiRequest({});
  const {
    run: runAdd,
    data: addResponse,
    requestStatus: addStatus,
    error: addError,
  } = useApiRequest({});

  const fetchAssessment = () =>
    id && runFetch(fetchCandidateAssessmentInfoService(id));

  useEffect(() => {
    fetchAssessment();
  }, [id]);

  const assessment = useMemo<AssessmentData | undefined>(() => {
    if (fetchResponse?.status === 200) {
      const data = fetchResponse.data.data;

      const assessment: AssessmentData = {
        title: data.title,

        id: data.assessment_id,
        _id: data.id,
        employer: {
          name: data.company_info.name,
          logo: data.company_info.company_logo ?? "",
        },
      };

      return assessment;
    } else if (error) {
      openToast({
        show: true,
        heading: "Sorry",
        text:
          error?.response?.data?.error?.message ??
          "Failed to fetch assessment information",
        type: "error",
        timeOut: 5000,
      });
    }
  }, [fetchResponse, fetchError]);

  const handleAbiaCheck = (email) => {
    if (id) run(abiaAcademyCheckService({ email, assessment: id }));
  };

  const handleAddCandidate = (data: addCandidateAssessmentRequestData) =>
    runAdd(abiaNewCandidateService(data));

  useMemo(() => {
    if (response?.status === 201) {
      const res = response.data.data;

      const data = res.response_data;

      setShowDetails(true);
      setCandidateDetails({
        firstName: res.verified_data["First Name"],
        lastName: res.verified_data["Last Name"],
        email: res.verified_data["Email"],
        dob: res.verified_data["Date of Birth"],
        gender: res.verified_data["Gender"],
        phone:
          extractAndFormatPhoneNumber(res.verified_data["Phone number"]) ?? "",
        assessmentStatus: data?.status ?? "Not Started",
      });

      openToast({
        show: true,
        heading: "Success",
        text: "Registration check successful",
        type: "success",
        timeOut: 5000,
      });
    } else if (error) {
      setShowDetails(false);
      setCandidateDetails({
        firstName: "",
        lastName: "",
        email: "",
        dob: "",
        assessmentStatus: "",
        phone: "",
        gender: "",
      });
      openToast({
        show: true,
        heading: "Sorry",
        text:
          error?.response?.data?.message ??
          error?.response?.data?.error?.message ??
          "Registration check failed, please try again or contact support",
        type: "error",
        timeOut: 5000,
      });
    }
  }, [response, error]);

  useMemo(() => {
    if (addResponse?.status === 201) {
      openToast({
        show: true,
        heading: "Success",
        text: "New candidate registered successfully!",
        type: "success",
        timeOut: 5000,
      });
    } else if (addResponse) {
      openToast({
        show: true,
        heading: "Sorry",
        text:
          addResponse?.response?.data?.message ??
          addResponse?.response?.data?.error?.message ??
          "Failed to add candidate, please try again or contact support",
        type: "error",
        timeOut: 5000,
      });
    }
  }, [addResponse, addError]);

  return (
    <>
      <Loader
        loading={
          requestStatus.isPending ||
          fetchStatus.isPending ||
          addStatus.isPending
        }
      />
      {assessment ? (
        <AbiaCheckRegistrationUI
          assessment={assessment}
          candidate={candidateDetails}
          showDetails={showDetails}
          handleCheck={handleAbiaCheck}
          handleReset={() => {
            setShowDetails(false);
            setCandidateDetails({
              firstName: "",
              lastName: "",
              email: "",
              dob: "",
              gender: "",
              phone: "",
              assessmentStatus: "",
            });
          }}
          handleAddCandidate={handleAddCandidate}
        />
      ) : null}
    </>
  );
};

export { CheckRegistration };

function extractAndFormatPhoneNumber(input) {
  let phone: string | null = null;

  // 1️⃣ Try to match tel: or number inside <a> tags
  const match = input.match(/tel:(\d+)|>(\d+)</i);
  if (match) {
    phone = match[1] || match[2];
  }

  // 2️⃣ If no match, try to find a plain number in the string
  if (!phone) {
    const plainMatch = input.match(/\d{7,}/); // Match at least 7 digits in a row
    phone = plainMatch ? plainMatch[0] : null;
  }

  if (!phone) return null;

  // 3️⃣ Clean and normalize
  phone = phone.replace(/\D/g, ""); // Remove non-digits

  // Ensure local Nigerian format starts with 0
  if (!phone.startsWith("0")) {
    phone = "0" + phone;
  }

  // Pad if less than 11 digits
  if (phone.length < 11) {
    phone = phone.padEnd(11, "0");
  }

  // Trim if more than 11 digits
  if (phone.length > 11) {
    phone = phone.slice(0, 11);
  }

  // Convert to +234 format
  return "+234" + phone.slice(1);
}
